/**
 * Live playlists pushed over the stream's status WebSocket (see apps/api/src/stream-room.ts).
 * The player's playlist loader reads from here instead of polling R2, so a new segment is
 * known within milliseconds of upload. Text only; segments still load from the CDN.
 */
export interface PushedPlaylist {
  seq: number;
  text: string;
  at: number;
}

const latest = new Map<string, PushedPlaylist>();
const lastServed = new Map<string, number>();
const waiters = new Map<string, Set<() => void>>();

export function publishPlaylist(streamId: string, seq: number, text: string) {
  const cur = latest.get(streamId);
  if (cur && cur.seq >= seq) return;
  latest.set(streamId, { seq, text, at: Date.now() });
  const ws = waiters.get(streamId);
  if (ws) for (const w of [...ws]) w();
}

/** Is the push feed alive (a playlist arrived recently)? Otherwise the player polls as usual. */
export function feedIsLive(streamId: string, maxAgeMs: number) {
  const cur = latest.get(streamId);
  return Boolean(cur && Date.now() - cur.at < maxAgeMs);
}

/**
 * The next playlist the player has not seen yet: immediately if one is waiting, otherwise as
 * soon as it is pushed (like an LL-HLS blocking reload), or null after `timeoutMs`.
 */
export function nextPlaylist(streamId: string, timeoutMs: number, signal: { aborted: boolean; onAbort?: () => void }): Promise<PushedPlaylist | null> {
  const take = () => {
    const cur = latest.get(streamId);
    if (cur && cur.seq > (lastServed.get(streamId) ?? -1)) {
      lastServed.set(streamId, cur.seq);
      return cur;
    }
    return null;
  };
  const ready = take();
  if (ready) return Promise.resolve(ready);
  return new Promise((resolve) => {
    let set = waiters.get(streamId);
    if (!set) waiters.set(streamId, (set = new Set()));
    const done = (value: PushedPlaylist | null) => {
      clearTimeout(timer);
      set!.delete(wake);
      resolve(value);
    };
    const wake = () => {
      const v = take();
      if (v) done(v);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    set.add(wake);
    signal.onAbort = () => done(null);
  });
}
